<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import TaccRelatedContactsAPI from 'dashboard/api/taccRelatedContacts';
import {
  setSelectedRelatedContactIds,
} from './relatedContactSelectionStore';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const isOpen = ref(false);
const contacts = ref([]);
const currentContact = ref(null);
const selectedIds = ref([]);

const contactId = contact => Number(contact?.id || 0);
const displayName = contact =>
  contact?.name || contact?.email || contact?.phone_number || 'Unnamed contact';
const initials = contact =>
  displayName(contact)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();

const selectedContacts = computed(() =>
  contacts.value.filter(contact => selectedIds.value.includes(contactId(contact)))
);

const label = computed(() => {
  if (!selectedContacts.value.length) return 'No related contacts selected';
  const first = displayName(selectedContacts.value[0]);
  return selectedContacts.value.length > 1
    ? `${first} + ${selectedContacts.value.length - 1}`
    : first;
});

const helper = computed(() => {
  const count = selectedContacts.value.length;
  if (!count) return 'No related contacts selected.';
  if (count === 1) {
    return `${displayName(selectedContacts.value[0])} will receive this as a separate WhatsApp message.`;
  }
  return `${displayName(selectedContacts.value[0])} and ${count - 1} others will each receive this as a separate WhatsApp message.`;
});

const metaFor = contact => {
  if (contact?.requires_template) {
    return 'Template required — outside the 24-hour window';
  }
  return contact?.description || contact?.email || '';
};

const syncSelection = () => {
  setSelectedRelatedContactIds(props.conversationId, selectedIds.value);
};

const refresh = async () => {
  if (!props.conversationId) return;
  try {
    const { data } = await TaccRelatedContactsAPI.getRelatedContacts(
      props.conversationId
    );
    contacts.value = data.contacts || [];
    currentContact.value = data.contact || null;
    selectedIds.value = contacts.value.map(contactId).filter(Boolean);
    syncSelection();
  } catch {
    contacts.value = [];
    currentContact.value = null;
    selectedIds.value = [];
    syncSelection();
  }
};

const toggleContact = contact => {
  const id = contactId(contact);
  if (!id) return;
  selectedIds.value = selectedIds.value.includes(id)
    ? selectedIds.value.filter(item => item !== id)
    : [...selectedIds.value, id];
  syncSelection();
};

watch(() => props.conversationId, refresh);
onMounted(refresh);
</script>

<template>
  <div v-if="contacts.length" class="relative mb-2">
    <button
      type="button"
      class="flex h-8 items-center gap-2 rounded-lg bg-n-alpha-1 px-3 text-sm text-n-slate-11 hover:bg-n-alpha-2"
      @click="isOpen = !isOpen"
    >
      <span class="font-medium text-n-slate-10">To:</span>
      <span class="max-w-[260px] truncate text-n-slate-12">{{ label }}</span>
      <span class="i-lucide-chevron-down size-3.5 text-n-slate-10" />
    </button>

    <div
      v-if="isOpen"
      class="absolute bottom-10 left-0 z-50 w-[342px] overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 shadow-xl"
    >
      <header class="px-5 py-4">
        <h3 class="m-0 text-base font-semibold text-n-slate-12">
          Message recipients
        </h3>
        <p class="m-0 mt-1 text-sm leading-5 text-n-slate-10">
          Selected contacts receive this as separate WhatsApp messages.
        </p>
      </header>
      <div class="h-px bg-n-weak" />

      <div v-if="currentContact" class="flex min-h-14 items-start gap-3 px-5 py-3">
        <span class="mt-1 flex size-4 shrink-0 items-center justify-center rounded bg-n-slate-5 text-white">
          <span class="i-lucide-check size-3" />
        </span>
        <span class="flex size-6 shrink-0 items-center justify-center rounded-full bg-n-amber-3 text-[10px] font-semibold text-n-amber-11">
          {{ initials(currentContact) }}
        </span>
        <span class="min-w-0 flex-1">
          <span class="flex items-center gap-2">
            <span class="truncate text-sm font-medium text-n-slate-12">
              {{ displayName(currentContact) }}
            </span>
            <span class="rounded bg-n-alpha-2 px-1.5 py-0.5 text-[10px] text-n-slate-10">
              Current conversation
            </span>
          </span>
        </span>
      </div>

      <div class="h-px bg-n-weak" />
      <p class="m-0 px-5 pt-3 text-[11px] font-semibold uppercase tracking-wide text-n-slate-10">
        Related contacts
      </p>
      <button
        v-for="(contact, index) in contacts"
        :key="contact.id"
        type="button"
        class="flex min-h-14 w-full items-start gap-3 px-5 py-3 text-left hover:bg-n-alpha-1"
        @click="toggleContact(contact)"
      >
        <span
          class="mt-1 flex size-4 shrink-0 items-center justify-center rounded border"
          :class="
            selectedIds.includes(contactId(contact))
              ? 'border-n-brand bg-n-brand text-white'
              : 'border-n-strong bg-transparent text-transparent'
          "
        >
          <span class="i-lucide-check size-3" />
        </span>
        <span
          class="flex size-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
          :class="
            index % 2
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-blue-3 text-n-blue-11'
          "
        >
          {{ initials(contact) }}
        </span>
        <span class="min-w-0 flex-1">
          <span class="block truncate text-sm font-medium text-n-slate-12">
            {{ displayName(contact) }}
          </span>
          <span
            v-if="metaFor(contact)"
            class="block truncate text-xs"
            :class="contact.requires_template ? 'text-n-amber-11' : 'text-n-slate-10'"
          >
            {{ metaFor(contact) }}
          </span>
        </span>
      </button>

      <div class="h-px bg-n-weak" />
      <footer class="flex items-center gap-3 px-5 py-3">
        <p class="m-0 flex-1 text-sm text-n-slate-10">
          {{ selectedContacts.length }} related contact{{ selectedContacts.length === 1 ? '' : 's' }} selected
        </p>
        <button
          type="button"
          class="rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white"
          @click="isOpen = false"
        >
          Done
        </button>
      </footer>
    </div>

    <p class="mt-2 flex items-center gap-2 text-xs text-n-slate-10">
      <span class="i-lucide-info size-3.5" />
      <span>{{ helper }}</span>
    </p>
  </div>
</template>
